import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Card,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  Icon,
  Separator,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Sortable context for SettingsGroup descendants
// ---------------------------------------------------------------------------
//
// SettingsGroup owns the DnD wiring. SettingsGroupRow opts in per row with
// `sortableId` and a hover-revealed grip is overlaid in the row's existing
// left-padding gutter — no other visual change to the row.

type SettingsGroupSortableConfig = Omittable<{
  items: string[];
  onReorder: (activeId: string, overId: string) => void;
  renderOverlay?: (id: string) => React.ReactNode;
}>;

interface SortableGroupContextValue {
  enabled: boolean;
  activeId: string | null;
}

const SortableGroupContext = React.createContext<SortableGroupContextValue>({
  enabled: false,
  activeId: null,
});

// ---------------------------------------------------------------------------
// SettingsGroup — outer wrapper with optional label, subtitle, and auto-separated Card
// ---------------------------------------------------------------------------

type SettingsGroupProps = Omittable<{
  label?: string;
  subtitle?: string;
  children: React.ReactNode;
  sortable?: SettingsGroupSortableConfig;
}>;

export function SettingsGroup({
  label,
  subtitle,
  children,
  sortable,
}: SettingsGroupProps) {
  const items = React.Children.toArray(children);
  const isMobile = useIsMobile();
  const [activeId, setActiveId] = useState<string | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(
    ...(sortable && !isMobile ? [pointerSensor] : []),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !sortable) return;
    sortable.onReorder(String(active.id), String(over.id));
  };

  const handleDragCancel = () => setActiveId(null);

  const contextValue = useMemo<SortableGroupContextValue>(
    () => ({ enabled: !!sortable && !isMobile, activeId }),
    [sortable, isMobile, activeId],
  );

  const card = (
    <Card>
      {items.map((child, index) => {
        if (index === 0) return <Fragment key={index}>{child}</Fragment>;
        const prev = items[index - 1];
        const afterHeader =
          React.isValidElement(prev) &&
          typeof prev.type === "function" &&
          (prev.type as any).__settingsGroupHeader === true;
        return (
          <Fragment key={index}>
            {afterHeader ? (
              <Separator />
            ) : (
              <Box paddingX="md">
                <Separator />
              </Box>
            )}
            {child}
          </Fragment>
        );
      })}
    </Card>
  );

  const header = label ? (
    <Box direction="column">
      <Text weight="semibold" size="sm">
        {label}
      </Text>
      {subtitle ? (
        <Text size="xs" variant="muted">
          {subtitle}
        </Text>
      ) : null}
    </Box>
  ) : null;

  if (!sortable) {
    return (
      <Box direction="column" gap="sm">
        {header}
        {card}
      </Box>
    );
  }

  return (
    <SortableGroupContext.Provider value={contextValue}>
      <Box direction="column" gap="sm">
        {header}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={sortable.items}
            strategy={verticalListSortingStrategy}
          >
            {card}
          </SortableContext>
          {!isMobile && sortable.renderOverlay ? (
            <DragOverlay dropAnimation={null} {...DND_OVERLAY_PROPS}>
              {activeId ? <Card>{sortable.renderOverlay(activeId)}</Card> : null}
            </DragOverlay>
          ) : null}
        </DndContext>
      </Box>
    </SortableGroupContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// RowLabel — shared label+subtitle+leading layout used by both row modes
// ---------------------------------------------------------------------------

function RowLabel({
  label,
  subtitle,
  leading,
}: Omittable<{
  label: React.ReactNode;
  subtitle?: string;
  leading?: React.ReactNode;
}>) {
  return (
    <Box direction="row" gap="md" align="center" shrink minWidth={0}>
      {leading}
      <Box direction="column" shrink minWidth={0}>
        <Text weight="medium" size="sm" leading="tight">
          {label}
        </Text>
        {subtitle ? (
          <Text size="xs" variant="muted" leading="tight">
            {subtitle}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsGroupRow — label+subtitle left, children right
//
// Three modes:
//   Static (no onClick):     children always visible, no hover effects
//   Pressable (onClick set): hover background, cursor pointer, hover-reveal children
//   Sortable (sortableId set): wraps in useSortable, overlays grip-vertical in
//                              the existing left-padding gutter on hover
// ---------------------------------------------------------------------------

type SettingsGroupRowProps = Omittable<{
  label: React.ReactNode;
  subtitle?: string;
  leading?: React.ReactNode;
  onClick?: () => void;
  actionsVisible?: boolean;
  sortableId?: string;
  children?: React.ReactNode;
}>;

export function SettingsGroupRow(props: SettingsGroupRowProps) {
  const { sortableId } = props;
  if (sortableId) {
    return <SortableRow {...props} sortableId={sortableId} />;
  }
  return <StaticRow {...props} />;
}

function StaticRow({
  label,
  subtitle,
  leading,
  onClick,
  actionsVisible,
  children,
}: SettingsGroupRowProps) {
  if (onClick) {
    return (
      // Not a real button element: rows host their own buttons and menu
      // triggers, and a button may not nest inside another button.
      <Box
        group="settings-row"
        direction="row"
        gap="md"
        align="center"
        justify="between"
        fill="width"
        paddingX="md"
        paddingY="md"
        hoverBackground
        cursor="pointer"
        role="button"
        tabIndex={0}
        onClick={() => onClick()}
      >
        <RowLabel label={label} subtitle={subtitle} leading={leading} />

        {children ? (
          <Box
            hoverReveal={actionsVisible ? undefined : "settings-row"}
            revealOnTouch
            direction="row"
            align="center"
            flex="none"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box direction="column" padding="md" justify="center" minHeight={62}>
      <Box direction="row" gap="lg" align="center" justify="between">
        <RowLabel label={label} subtitle={subtitle} leading={leading} />
        {children ? (
          <Box direction="row" align="end" flex="none">
            {children}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function SortableRow({
  label,
  subtitle,
  leading,
  sortableId,
  children,
}: SettingsGroupRowProps & { sortableId: string }) {
  const { enabled } = React.useContext(SortableGroupContext);
  const isMobile = useIsMobile();
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: sortableId,
    disabled: !enabled || isMobile,
  });

  const draggable = enabled && !isMobile;

  return (
    <Box
      group="settings-row"
      ref={setNodeRef}
      // Box carries no motion props — dnd-kit streams the transform every
      // frame, so it stays an inline style.
      style={{
        transform: CSS.Transform.toString(transform) ?? undefined,
        transition: isSorting ? transition : undefined,
        opacity: isDragging ? 0.3 : undefined,
      }}
      cursor={!draggable ? undefined : isDragging ? "grabbing" : "grab"}
      position="relative"
      {...(draggable ? (attributes as any) : undefined)}
      {...(draggable ? (listeners as any) : undefined)}
      {...(draggable ? DND_DRAGGABLE_PROPS : undefined)}
    >
      {draggable ? (
        <Box
          hoverReveal="settings-row"
          revealOnTouch
          className="pointer-events-none"
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width={16}
          justify="center"
          align="center"
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      ) : null}
      <Box direction="column" padding="md" justify="center" minHeight={62}>
        <Box direction="row" gap="lg" align="center" justify="between">
          <RowLabel label={label} subtitle={subtitle} leading={leading} />
          {children ? (
            <Box
              direction="row"
              align="end"
              flex="none"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {children}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsGroupHeader — count + action row (e.g. "5 webhooks" + plus button)
// ---------------------------------------------------------------------------

interface SettingsGroupHeaderProps {
  children: React.ReactNode;
}

export function SettingsGroupHeader({ children }: SettingsGroupHeaderProps) {
  return (
    <Box direction="row" align="center" justify="between" paddingX="md" paddingY="md">
      {children}
    </Box>
  );
}
(SettingsGroupHeader as any).__settingsGroupHeader = true;

// ---------------------------------------------------------------------------
// SettingsGroupContent — arbitrary padded content (forms, custom layouts)
// ---------------------------------------------------------------------------

interface SettingsGroupContentProps {
  children: React.ReactNode;
}

export function SettingsGroupContent({ children }: SettingsGroupContentProps) {
  return <Box padding="md">{children}</Box>;
}
